import { ExternalLink } from 'lucide-react';
import { PLATFORMS } from '@/lib/platforms';

// Order of value-chain stages, mapped to platform slugs.
const STAGES = [
  { n: '01', stage: 'Plan',     kicker: 'Market intelligence',     slugs: ['motoinsights'] },
  { n: '02', stage: 'Sell',     kicker: 'Dealer & retail ops',     slugs: ['dlip', 'otobot'] },
  { n: '03', stage: 'Buy',      kicker: 'Marketplaces',            slugs: ['trucksbuses'] },
  { n: '04', stage: 'Connect',  kicker: 'Lifecycle data',          slugs: ['vehicleledger'] },
  { n: '05', stage: 'Service',  kicker: 'Workshop & aftersales',   slugs: ['autorox', 'baywise'] },
  { n: '06', stage: 'Maintain', kicker: 'Parts & aftermarket',     slugs: ['autozilla'] },
  { n: '07', stage: 'Listen',   kicker: 'Quality & claims',        slugs: ['carbikeclaims'] },
];
const SPINE_SLUG = 'autodata-exchange';

const find = (slug) => PLATFORMS.find((p) => p.slug === slug);

const PlatformChip = ({ platform }) => {
  const built = platform.type === 'built';
  const tone = built
    ? 'border-amber/50 bg-amber/10 hover:bg-amber/20 text-amber'
    : 'border-cyan/50 bg-cyan/10 hover:bg-cyan/20 text-cyan';
  return (
    <a
      href={platform.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`group block border p-3 transition-all ${tone}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="font-display text-base md:text-lg leading-tight">
          {platform.name}
        </span>
        <ExternalLink className="h-3 w-3 opacity-50 group-hover:opacity-100 mt-1 shrink-0" />
      </div>
      <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-400 leading-tight">
        {built ? 'Built' : 'Backed'}
      </div>
    </a>
  );
};

export const VisionValueChain = () => {
  const spine = find(SPINE_SLUG);
  const builtCount = PLATFORMS.filter((p) => p.type === 'built').length;
  const backedCount = PLATFORMS.filter((p) => p.type === 'invested').length;

  return (
    <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-black border-y border-white/10">
      {/* Header */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end">
        <div className="lg:col-span-7">
          <div className="overline text-amber mb-4">
            <span className="inline-flex items-center gap-2">
              <span className="h-px w-6 bg-amber inline-block" />
              Vision · across the automotive value chain
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-balance leading-[1.05]">
            Present everywhere the vehicle goes.
          </h2>
          <p className="mt-5 text-zinc-400 leading-relaxed max-w-2xl">
            From planning a car to claiming on it — we are in the data, the
            workflow, or the capital. Through the platforms we build and
            back, we cover every stage of the Indian automotive value
            chain.
          </p>
        </div>
        <div className="lg:col-span-5 lg:pl-10 border-l border-white/10">
          <div className="grid grid-cols-3 gap-px bg-white/5 border border-white/10">
            <div className="bg-ink-900 p-5">
              <div className="font-display text-3xl text-amber tabular-nums">{builtCount}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2 leading-snug">
                Platforms built
              </div>
            </div>
            <div className="bg-ink-900 p-5">
              <div className="font-display text-3xl text-cyan tabular-nums">{backedCount}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2 leading-snug">
                Platforms backed
              </div>
            </div>
            <div className="bg-ink-900 p-5">
              <div className="font-display text-3xl text-white tabular-nums">{STAGES.length}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2 leading-snug">
                Stages covered
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Value chain visual */}
      <div className="bg-ink-900 border border-white/10 p-6 md:p-8 overflow-x-auto relative">
        <div className="md:hidden absolute right-3 top-3 z-10 font-mono text-[9px] uppercase tracking-[0.18em] text-amber bg-ink-900/90 border border-amber/30 px-2 py-1 pointer-events-none">
          Swipe →
        </div>
        <div className="min-w-[920px]">
          {/* Stage headers + connector spine */}
          <div className="relative">
            <div className="grid grid-cols-7 gap-3 mb-2">
              {STAGES.map((s) => (
                <div key={s.n} className="flex flex-col">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber mb-2">
                    / {s.n}
                  </div>
                  <div className="font-display text-2xl text-white leading-none">
                    {s.stage}
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-500 mt-1.5 leading-tight">
                    {s.kicker}
                  </div>
                </div>
              ))}
            </div>

            {/* Connector line with arrows */}
            <div className="relative my-5">
              <div className="h-px bg-gradient-to-r from-amber/10 via-amber/50 to-amber/10" />
              <div className="absolute inset-0 grid grid-cols-7 gap-3 items-center">
                {STAGES.map((s) => (
                  <div key={s.n} className="flex items-center justify-center">
                    <div className="grid place-items-center h-4 w-4 rounded-full bg-ink-900 border border-amber/60">
                      <div className="h-1.5 w-1.5 rounded-full bg-amber" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Platform chips per stage */}
            <div className="grid grid-cols-7 gap-3">
              {STAGES.map((s) => (
                <div key={s.n} className="flex flex-col gap-2">
                  {s.slugs.map((slug) => {
                    const p = find(slug);
                    if (!p) return null;
                    return <PlatformChip key={slug} platform={p} />;
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Data spine — AutoData.Exchange spans everything */}
          {spine && (
            <div className="mt-8 pt-6 border-t border-dashed border-amber/30">
              <div className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-3 md:col-span-2">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber mb-1">
                    Data spine
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-500 leading-tight">
                    Underlying every stage
                  </div>
                </div>
                <div className="col-span-9 md:col-span-10">
                  <a
                    href={spine.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 border border-amber/40 bg-amber/5 hover:bg-amber/15 transition-colors p-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-display text-xl text-amber">{spine.name}</span>
                        <ExternalLink className="h-3.5 w-3.5 text-amber opacity-60 group-hover:opacity-100" />
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-400">
                        Neutral lifecycle data layer connecting OEMs, insurers, lenders, workshops
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-1">
                      {Array.from({ length: 7 }).map((_, i) => (
                        <span key={i} className="h-1.5 w-1.5 rounded-full bg-amber/70" />
                      ))}
                    </div>
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="mt-6 flex flex-wrap items-center gap-6 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 border border-amber bg-amber/30" />
              Built
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 border border-cyan bg-cyan/30" />
              Backed
            </span>
            <span className="ml-auto opacity-70">
              Tap any platform to visit it
            </span>
          </div>
        </div>
      </div>

      {/* Closing line */}
      <p className="mt-8 max-w-3xl text-zinc-400 leading-relaxed">
        From the moment a vehicle is conceived to the moment it changes hands
        the third time — a venture we operate, or one we back, is somewhere
        in the loop. <span className="text-amber">That's the north star.</span>
      </p>
    </section>
  );
};

export default VisionValueChain;
